import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  LogoPlacement,
  BRAND_BAND_HEIGHT_PX,
  DEFAULT_LOGO_WIDTH_PCT,
} from '../utils/logoPlacement';

interface LogoPlacementEditorProps {
  /** Data URL of the logo image (null → nothing to place). */
  logoDataUrl?: string | null;
  /** Current placement (percentages). */
  placement: LogoPlacement;
  /** Called with the new placement as the user drags/resizes. */
  onChange: (placement: LogoPlacement) => void;
  companyName?: string;
  headerText?: string;
}

// Representative A4 content width (px @ ~96dpi, ~1.5cm margins). Used only to
// give the preview band the same width:height ratio as the exported PDF so the
// logo looks proportionally identical.
const REF_CONTENT_WIDTH_PX = 680;
const BAND_ASPECT = BRAND_BAND_HEIGHT_PX / REF_CONTENT_WIDTH_PX; // height / width

type DragMode = 'move' | 'resize' | null;

export const LogoPlacementEditor: React.FC<LogoPlacementEditorProps> = ({
  logoDataUrl,
  placement,
  onChange,
  companyName,
  headerText,
}) => {
  const bandRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ mode: DragMode; grabX: number; grabY: number }>({
    mode: null,
    grabX: 0,
    grabY: 0,
  });
  const [activeMode, setActiveMode] = useState<DragMode>(null);

  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const band = bandRef.current;
      const { mode, grabX, grabY } = dragRef.current;
      if (!band || !mode) return;
      const rect = band.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      if (mode === 'move') {
        const leftPx = e.clientX - rect.left - grabX;
        const topPx = e.clientY - rect.top - grabY;
        let xPct = (leftPx / rect.width) * 100;
        let yPct = (topPx / rect.height) * 100;
        // Keep the logo within the band horizontally; clamp top within band.
        xPct = clamp(xPct, 0, 100 - placement.width);
        const logoHpct = logoRef.current
          ? (logoRef.current.offsetHeight / rect.height) * 100
          : 0;
        yPct = clamp(yPct, 0, Math.max(0, 100 - logoHpct));
        onChange({ ...placement, x: xPct, y: yPct });
      } else if (mode === 'resize') {
        const logoLeftPx = (placement.x / 100) * rect.width;
        const newWidthPx = e.clientX - rect.left - logoLeftPx;
        let widthPct = (newWidthPx / rect.width) * 100;
        widthPct = clamp(widthPct, 5, 100 - placement.x);
        onChange({ ...placement, width: widthPct });
      }
    },
    [placement, onChange]
  );

  const stopDrag = useCallback(() => {
    dragRef.current.mode = null;
    setActiveMode(null);
  }, []);

  useEffect(() => {
    if (!activeMode) return;
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDrag);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDrag);
    };
  }, [activeMode, handlePointerMove, stopDrag]);

  const startMove = (e: React.PointerEvent) => {
    if (!logoRef.current || !bandRef.current) return;
    e.preventDefault();
    const logoRect = logoRef.current.getBoundingClientRect();
    dragRef.current = {
      mode: 'move',
      grabX: e.clientX - logoRect.left,
      grabY: e.clientY - logoRect.top,
    };
    setActiveMode('move');
  };

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { mode: 'resize', grabX: 0, grabY: 0 };
    setActiveMode('resize');
  };

  return (
    <div className="space-y-3">
      {/* Document mockup */}
      <div
        className="mx-auto bg-gray-100 dark:bg-gray-900 rounded-lg p-4 select-none"
        style={{ maxWidth: 520 }}
      >
        <div
          className="bg-white shadow-md mx-auto"
          style={{ width: '100%', aspectRatio: '1 / 1.414', padding: '8%' }}
        >
          {/* Header band */}
          <div
            ref={bandRef}
            className="relative w-full border-b-2 border-[#1993e5]"
            style={{ paddingBottom: `${BAND_ASPECT * 100}%`, height: 0 }}
          >
            {logoDataUrl ? (
              <img
                ref={logoRef}
                src={logoDataUrl}
                alt="Company logo"
                draggable={false}
                onPointerDown={startMove}
                className={`absolute object-contain ${
                  activeMode === 'move' ? 'cursor-grabbing' : 'cursor-grab'
                }`}
                style={{
                  left: `${placement.x}%`,
                  top: `${placement.y}%`,
                  width: `${placement.width}%`,
                  maxHeight: '100%',
                  height: 'auto',
                  outline: '1px dashed rgba(25,147,229,0.6)',
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
                Upload a logo to position it
              </div>
            )}
            {/* Resize handle */}
            {logoDataUrl && (
              <div
                onPointerDown={startResize}
                className="absolute bg-[#1993e5] rounded-sm cursor-nwse-resize"
                style={{
                  width: 12,
                  height: 12,
                  // Anchor to the logo's bottom-right corner (approx via x+width).
                  left: `calc(${placement.x + placement.width}% - 6px)`,
                  top: `${placement.y}%`,
                  marginTop: -6,
                  transform: 'translateY(0)',
                }}
                title="Drag to resize"
              />
            )}
          </div>

          {/* Faux document body for context */}
          <div className="mt-3">
            {companyName && (
              <div className="text-[10px] font-bold text-gray-800">{companyName}</div>
            )}
            {headerText && (
              <div className="text-[8px] text-gray-400">{headerText}</div>
            )}
            <div className="mt-2 h-3 w-2/3 rounded bg-gray-300" />
            <div className="mt-3 space-y-1.5">
              {Array.from({ length: 7 }).map((_, i) => (
                <div
                  key={i}
                  className="h-1.5 rounded bg-gray-200"
                  style={{ width: `${90 - (i % 3) * 12}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Width slider + reset */}
      <div className="flex items-center gap-3">
        <label className="text-xs text-gray-600 dark:text-gray-400 w-12">Size</label>
        <input
          type="range"
          min={5}
          max={100}
          step={1}
          value={Math.round(placement.width)}
          onChange={(e) => {
            const width = Number(e.target.value);
            onChange({ ...placement, x: clamp(placement.x, 0, 100 - width), width });
          }}
          className="flex-1 accent-[#1993e5]"
        />
        <span className="text-xs tabular-nums text-gray-500 w-10 text-right">
          {Math.round(placement.width)}%
        </span>
        <button
          type="button"
          onClick={() =>
            onChange({ width: DEFAULT_LOGO_WIDTH_PCT, x: 100 - DEFAULT_LOGO_WIDTH_PCT, y: 0 })
          }
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          title="Reset placement"
        >
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Drag the logo to position it; drag the blue handle (or use the slider) to resize.
        This is how it will appear at the top of exported documents.
      </p>
    </div>
  );
};
